<template>
  <div>
    <!-- ========== IMAGEN DECORATIVA SVG (Estética de fondo) ========== -->
    <svg class="login__blob" viewBox="0 0 900 840">
      <mask id="mask0" mask-type="alpha">
        <path d="M342.407 73.6315C388.53 56.4007 394.378 17.3643 391.538 
         0H900V840H0C14.5385 834.991 100.266 804.436 77.2046 707.263C49.6393 
         591.11 115.306 518.927 176.468 488.873C363.385 397.026 156.98 302.824 
         167.945 179.32C173.46 117.209 284.755 95.1699 342.407 73.6315Z" />
      </mask>
      <g mask="url(#mask0)">
        <path d="M342.407 73.6315C388.53 56.4007 394.378 17.3643 391.538 
         0H900V840H0C14.5385 834.991 100.266 804.436 77.2046 707.263C49.6393 
         591.11 115.306 518.927 176.468 488.873C363.385 397.026 156.98 302.824 
         167.945 179.32C173.46 117.209 284.755 95.1699 342.407 73.6315Z" />
        <image class="login__img" href="../assets/login-img/bg-img.jpg" />
      </g>
    </svg>

    <!-- ========== CONTENEDOR PRINCIPAL DE LOGIN ========== -->
    <div class="login container grid">
      <div class="login__access">
        <!-- ========== TÍTULO DE BIENVENIDA ========== -->
        <h1 class="login__title">
          <span class="login__title-highlight">¡Bienvenido(a)!</span><br />
          Inicia sesión en tu <span class="login__title-underline">cuenta</span>
        </h1>

        <div class="login__area">
          <!-- ========== FORMULARIO DE INICIO DE SESIÓN ========== -->
          <form class="login__form" @submit.prevent="handleLogin">
            <div class="login__content grid">
              <div>
                <!-- ========== CAMPO DE USUARIO ========== -->
                <div class="login__box" :class="{ 'error-border': usernameError, 'focused': focusedField === 'username' }">
                  <input
                    type="text"
                    id="username"
                    v-model="username"
                    placeholder=" "
                    class="login__input"
                    @focus="focusedField = 'username'"
                    @blur="focusedField = ''; validateUsername()"
                    required
                  />
                  <label
                    for="username"
                    class="login__label"
                    :class="{ active: focusedField === 'username' || username, 'error-label': usernameError }"
                  >Usuario</label>
                  <ion-icon name="person-outline" class="login__icon"></ion-icon>
                </div>
                <span v-if="usernameError" class="login__error">{{ usernameError }}</span>
              </div>

              <div>
                <!-- ========== CAMPO DE CONTRASEÑA ========== -->
                <div class="login__box" :class="{ 'error-border': passwordError, 'focused': focusedField === 'password' }">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    id="password"
                    v-model="password"
                    placeholder=" "
                    class="login__input"
                    @focus="focusedField = 'password'"
                    @blur="focusedField = ''; validatePassword()"
                    required
                  />
                  <label
                    for="password"
                    class="login__label"
                    :class="{ active: focusedField === 'password' || password, 'error-label': passwordError }"
                  >Contraseña</label>
                  <ion-icon
                    :name="showPassword ? 'eye-outline' : 'eye-off-outline'"
                    class="login__icon login__password"
                    @click="showPassword = !showPassword"
                  ></ion-icon>
                </div>
                <span v-if="passwordError" class="login__error">{{ passwordError }}</span>
              </div>
            </div>

            <!-- ========== BOTÓN DE ENVÍO E INDICADORES ========== -->
            <button type="submit" class="login__button" :disabled="isLoading">
              <span v-if="!isLoading">Iniciar sesión</span>
              <span v-else class="loading-spinner"></span>
            </button>

            <!-- ========== MENSAJES DE ESTADO ========== -->
            <p v-if="cooldownMessage" class="cooldown-message">
              {{ cooldownMessage }}
            </p>
            <p v-if="errorMessage" class="error-message">
              {{ errorMessage }}
            </p>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  // ====================
  // Datos reactivos del componente
  // ====================
  data() {
    return {
      username: "",           // Usuario ingresado
      password: "",           // Contraseña ingresada
      showPassword: false,     // Mostrar u ocultar contraseña

      usernameError: '',       // Error de validación para el usuario
      passwordError: '',       // Error de validación para la contraseña

      errorMessage: "",        // Error general (backend o red)
      cooldownMessage: "",     // Mensaje de cooldown por intentos fallidos
      cooldownTime: 0,         // Tiempo restante de cooldown (en segundos)
      isLoading: false,        // Estado de carga del botón

      focusedField: ''         // Campo actualmente enfocado (para efectos visuales)
    };
  },

  // ====================
  // Ciclo de vida
  // ====================
  mounted() {
    // Si hay una sesión expirada guardada, mostrar mensaje
    const sessionExpired = localStorage.getItem("expiredSession");
    if (sessionExpired) {
      this.errorMessage = "Sesión expirada. Inicia sesión nuevamente.";
      localStorage.removeItem("expiredSession");
    }
  },

  // ====================
  // Métodos del componente
  // ====================
  methods: {
    // Validación en vivo del campo de usuario
    validateUsername() {
      this.usernameError = this.username.trim() === ''
        ? 'El usuario es requerido.'
        : '';
    },

    // Validación en vivo del campo de contraseña
    validatePassword() {
      this.passwordError = this.password.trim() === ''
        ? 'La contraseña es requerida.'
        : '';
    },

    // Maneja el envío del formulario de login
    async handleLogin() {
      // Resetear mensajes
      this.errorMessage = "";
      this.cooldownMessage = "";
      this.isLoading = true;

      try {
        // Enviar solicitud de login al backend
        const response = await this.$axios.post("/auth/login", {
          username: this.username,
          password: this.password
        });

        // Guardar datos en localStorage
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user_role", response.data.user.role);
        localStorage.setItem("user_id", response.data.user.id);

        // Redirigir al dashboard
        this.$router.push("/metrics");

      } catch (error) {
        // Manejo de errores
        if (error.response) {
          if (error.response.status === 429) {
            // Extraer segundos del mensaje de error (si existen)
            const match = error.response.data.error.match(/\d+/);
            this.cooldownTime = match ? parseInt(match[0]) : 0;

            if (this.cooldownTime > 0) {
              this.startCooldown();
              this.cooldownMessage = `Demasiados intentos. Intenta de nuevo en ${this.cooldownTime} segundos.`;
            }
          } else {
            this.errorMessage = error.response.data.error || "Error al iniciar sesión.";
          }
        } else {
          this.errorMessage = "No se pudo conectar con el servidor.";
        }
      } finally {
        this.isLoading = false;
      }
    },

    // Inicia el temporizador de cooldown
    startCooldown() {
      const interval = setInterval(() => {
        if (this.cooldownTime > 0) {
          this.cooldownTime -= 1;
          this.cooldownMessage = `Demasiados intentos. Intenta de nuevo en ${this.cooldownTime} segundos.`;
        } else {
          this.cooldownMessage = "";
          clearInterval(interval);
        }
      }, 1000);
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&display=swap");

* {
  box-sizing: border-box;
  padding: 0;
  margin: 0;
}

body,
input,
button {
  font-family: "Poppins", system-ui;
  font-size: 0.938rem;
  background-color: #ffffff;
  color: #6c757d;
}

input,
button {
  border: none;
  outline: none;
}

a {
  text-decoration: none;
}

img {
  display: block;
  max-width: 100%;
  height: auto;
}

.container {
  max-width: 1120px;
  margin-inline: 1.5rem;
}

.grid {
  display: grid;
  gap: 1rem;
}

/* ------------------------------
   Login - layout principal
--------------------------------*/
.login {
  position: relative;
  max-height: 90vh;
  margin-top: 3rem;
  align-items: start;
  overflow: hidden;
  display: grid;
}

/* Blob decorativo (desktop) */
.login__blob {
  display: none;
}

/* ------------------------------
   Títulos y encabezados
--------------------------------*/
.login__title {
  font-size: 2rem;
  color: #0f172a;
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.3;
  font-weight: 600;
  animation: fadeDown 0.6s ease-in-out;
  position: relative;
  font-family: 'Manrope', sans-serif;
}

.login__title-highlight {
  display: inline-block;
  color: #007bff;
  font-weight: 800;
  font-size: 3.3rem;
  letter-spacing: -0.5px;
}

.login__title-underline {
  position: relative;
  z-index: 1;
}

.login__title-underline::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0.1em;
  width: 100%;
  height: 0.25em;
  background-color: #007bff;
  opacity: 0.3;
  z-index: -1;
  border-radius: 2px;
}

/* ------------------------------
   Input y cajas de formulario
--------------------------------*/
.login__box {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #f1f5f9;
  border-radius: 1rem;
  animation: fadeDown 0.8s ease-in-out;
}

.login__input {
  background: none;
  width: 100%;
  padding: 2rem 2.5rem 1rem 1.25rem;
  font-weight: 600;
  font-size: 1rem;
  border: 3px solid transparent;
  border-radius: 1rem;
  z-index: 1;
  transition: border-color 0.4s;
  color: #0f172a;
  box-sizing: border-box;
}

/* Etiquetas flotantes */
.login__label {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  transition:
    top 0.6s ease,
    transform 0.6s ease,
    font-size 0.6s ease,
    color 0.6s ease;
  color: #6c757d;
  background: #f1f5f9;
  padding: 0 5px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.login__label.active {
  top: 1.2rem;
  font-size: 0.8rem;
  color: #007bff;
}

.login__label.error-label {
  color: #ec1a03;
}

/* Íconos de campos */
.login__icon {
  position: absolute;
  right: 1rem;
  font-size: 1.25rem;
  transition: color 0.4s;
  color: #6c757d;
}

.login__password {
  cursor: pointer;
  z-index: 10;
}

/* ------------------------------
   Estados de error y focus
--------------------------------*/
.login__box.focused .login__input {
  border-color: #007bff;
  box-shadow: 0 0 6px rgba(0, 123, 255, 0.4);
}

.login__box.focused .login__icon {
  color: #007bff;
}

.login__box.error-border .login__input {
  border-color: #ec1a03;
  box-shadow: 0 0 6px rgba(236, 26, 3, 0.4);
}

.login__box.error-border .login__icon {
  color: #ec1a03;
}

/* Mensajes de error */
.login__error {
  color: #ec1a03;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: block;
  font-weight: 600;
  text-align: center;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* ------------------------------
   Botón principal
--------------------------------*/
.login__button {
  width: 100%;
  display: inline-flex;
  justify-content: center;
  background-color: #007bff;
  color: #fff;
  font-weight: 600;
  padding-block: 1.5rem;
  border-radius: 4rem;
  margin-block: 2rem;
  cursor: pointer;
  transition: background-color 0.4s, box-shadow 0.4s;
  animation: fadeDown 1s ease-in-out;
}

.login__button:hover {
  background-color: #0069d9;
  box-shadow: 0 8px 24px rgba(0, 123, 255, 0.3);
}

/* ------------------------------
   Indicador de carga (spinner)
--------------------------------*/
.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid white;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* ------------------------------
   Mensajes de error y espera
--------------------------------*/
.cooldown-message {
  color: #f39c12;
  font-size: 0.9rem;
  font-weight: bold;
  text-align: center;
}

.error-message {
  color: #e74c3c;
  font-size: 0.9rem;
  font-weight: bold;
  text-align: center;
}

/* ------------------------------
   Animaciones
--------------------------------*/
@keyframes fadeDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ------------------------------
   Responsive - Móviles
--------------------------------*/
@media screen and (max-width: 540px) {
  .login__title {
    font-size: 1.5rem;
  }
  .login__title-highlight {
    font-size: 2rem;
  }
  .login__title-underline {
    font-size: 1.4rem;
  }
}

/* ------------------------------
   Responsive - Escritorio
--------------------------------*/
@media screen and (min-width: 1150px) {
  .container {
    margin-inline: auto;
  }

  .login__area {
    width: 460px;
    margin: initial;
  }

  .login__access {
    width: 600px;
  }

  .login__title {
    text-align: initial;
    margin-bottom: 3rem;
  }

  .login__button {
    margin-bottom: 3rem;
  }

  .login__blob {
    display: block;
    height: 100vh;
    position: absolute;
    top: 0;
    right: 0;
  }

  .login__img {
    transform: translate(0rem, 0rem);
  }
}
</style>